import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type { AttachTarget, MetaAdPublishStatus } from '../../../types/metaAdDraft';
import { destinationLabel } from '../liveAdSetFlow';
import { mediaModeOf } from '../../../lib/metaAttachMedia';

type Props = {
  open: boolean;
  target: AttachTarget;
  accountName: string;
  adName: string;
  status: MetaAdPublishStatus;
  publishing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="text-sm text-slate-800">{value}</dd>
    </div>
  );
}

// Confirmation for "Add an ad to a live ad set". It says plainly that ONE ad is added
// and nothing else changes, and what an Active ad does to spend.
export function AttachPublishConfirmationDialog({ open, target, accountName, adName, status, publishing, onCancel, onConfirm }: Props) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !publishing) onCancel();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add this ad to the live ad set?</DialogTitle>
          <DialogDescription>
            This adds one new ad. The campaign, the ad set and its other ads are not changed. Once published, the draft is read-only.
          </DialogDescription>
        </DialogHeader>
        <dl className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3">
          <Row label="Account" value={accountName} />
          <Row label="Campaign" value={target.campaignName || target.campaignId} />
          <Row label="Ad set" value={target.adSetName || target.adSetId} />
          <Row label="Destination" value={destinationLabel(target.destination)} />
          <Row label="New ad" value={adName} />
          <Row
            label="Media"
            value={
              mediaModeOf(target) === 'exact'
                ? 'The source creative, unchanged'
                : mediaModeOf(target) === 'original'
                  ? "The source ad's original image or video"
                  : 'New upload'
            }
          />
          <Row label="Status" value={status === 'ACTIVE' ? 'Active' : 'Paused'} />
        </dl>
        {status === 'ACTIVE' && (
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            Active starts spending inside this ad set's existing budget straight away.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={publishing}>
            Cancel
          </Button>
          <Button autoFocus onClick={onConfirm} disabled={publishing}>
            {publishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publishing…
              </>
            ) : (
              'Add ad to ad set'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
